import { Toaster, toast } from 'sonner'
import { cn } from '@/lib/utils'

export type AppToastType = 'success' | 'error' | 'info'

interface AppToastCardProps {
  type: AppToastType
  message: unknown
}

interface ToastStatusIconProps {
  type: AppToastType
}

const TOAST_ICON_CLASSES: Record<AppToastType, string> = {
  success: 'text-success',
  error: 'text-error',
  info: 'text-info',
}

const ToastStatusIcon = ({ type }: ToastStatusIconProps) => (
  <svg
    className={cn('h-4 w-4 shrink-0', TOAST_ICON_CLASSES[type])}
    aria-hidden='true'
    focusable='false'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 16 16'
  >
    <circle cx='8' cy='8' r='7' stroke='currentColor' strokeWidth='1.5' />
    {type === 'success' ? (
      <path
        d='M4.75 8.2 6.75 10.2 11.25 5.8'
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='1.5'
      />
    ) : null}
    {type === 'error' ? (
      <path
        d='m5.5 5.5 5 5m0-5-5 5'
        stroke='currentColor'
        strokeLinecap='round'
        strokeWidth='1.5'
      />
    ) : null}
    {type === 'info' ? (
      <>
        <path
          d='M8 7.25v4'
          stroke='currentColor'
          strokeLinecap='round'
          strokeWidth='1.5'
        />
        <circle cx='8' cy='4.75' r='.75' fill='currentColor' />
      </>
    ) : null}
  </svg>
)

const toDisplayText = (message: unknown): string => {
  if (typeof message === 'string') return message
  try {
    return JSON.stringify(message) ?? 'Unknown error'
  } catch {
    return 'Unknown error'
  }
}

/**
 * Compact top-center toast matching the MyAssetMap design system: black
 * surface, 8px/12px padding, 4px gap, and rounded 8px corners.
 */
const AppToastCard = ({ type, message }: AppToastCardProps) => (
  <div
    role='alert'
    className='max-w-[calc(100vw-32px)] text-xs font-semibold leading-5 text-white shadow-lg'
    style={{
      display: 'flex',
      padding: 'var(--spacing-sm-4, 8px) var(--spacing-sm-6, 12px)',
      alignItems: 'center',
      gap: 'var(--spacing-sm-2, 4px)',
      alignSelf: 'stretch',
      borderRadius: 'var(--radius-md, 8px)',
      background: 'var(--surface-black, #000)',
    }}
  >
    <ToastStatusIcon type={type} />
    <span>{toDisplayText(message)}</span>
  </div>
)

/**
 * Imperatively shows an app toast. Replaces react-alert's
 * `useAlert().success/error/info(message, { timeout })` calls.
 *
 * @param type    success | error | info
 * @param message Toast body; non-strings are JSON-stringified like the old
 *                react-alert template did.
 * @param timeout Auto-dismiss duration in milliseconds.
 */
export const showAppToast = (
  type: AppToastType,
  message: unknown,
  timeout: number
): void => {
  toast.custom(() => <AppToastCard type={type} message={message} />, {
    duration: timeout,
  })
}

/**
 * Global toast outlet (sonner). Mount once near the app root; replaces the
 * react-alert `<AlertProvider>` wrapper. Both shells mount it.
 *
 * `z-toast` places toasts above every dialog (MAM-952). It needs `!`: sonner
 * appends its own `[data-sonner-toaster] { z-index: 999999999 }` style tag at
 * runtime, after the app stylesheet, so an equal-specificity class loses.
 */
export const AppToaster = () => (
  <Toaster
    position='top-center'
    offset={{ top: 12 }}
    mobileOffset={{ top: 12, right: 16, left: 16 }}
    gap={8}
    visibleToasts={5}
    className='z-toast!'
  />
)
